import React, { useEffect, useMemo, useState } from "react";
import { Modal, Form, Row, Col, Alert, Spinner, Button } from "react-bootstrap";
import { CheckCircleFill, XCircleFill } from "react-bootstrap-icons";
import axios from "axios";

const NAV_BLUE = "#183B72";
const SUCCESS_GREEN = "#18B394";

function formatDisplayDate(dateString) {
  if (!dateString) return "-";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function getDaysLeft(dateString) {
  if (!dateString) return null;
  const today = new Date();
  const expiry = new Date(dateString);

  if (Number.isNaN(expiry.getTime())) return null;

  today.setHours(0, 0, 0, 0);
  expiry.setHours(0, 0, 0, 0);

  return Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
}

// Cannot Submit if upload same expired Date
function normalizeDateOnly(value) {
  if (!value) return "";

  // yyyy-mm-dd
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return value;
  }

  const d = new Date(value);

  if (Number.isNaN(d.getTime())) {
    return "";
  }

  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");

  return `${yyyy}-${mm}-${dd}`;
}

export default function LicenseRenewalModal({
  show,
  onHide,
  profile,
  authToken,
  analyzeEndpoint,
  submitEndpoint,
  onSubmitted,
  editRecord = null,
}) {
  const [form, setForm] = useState({
    id: "",
    refNumber: "",
    photo: null,
    photoName: "",
    expiryDate: "",
    confirm: false,
  });

  const [previewUrl, setPreviewUrl] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [badgeChecking, setBadgeChecking] = useState(false);
  const [badgeVerified, setBadgeVerified] = useState(false);
  const [badgeAnalyzeResult, setBadgeAnalyzeResult] = useState(null);
  const [error, setError] = useState("");

  const [fileMeta, setFileMeta] = useState({
    filename: "",
    path: "",
    mimetype: "",
    size: 0,
  });

  const [submitDone, setSubmitDone] = useState(false);
  const [submittedRefNo, setSubmittedRefNo] = useState("");

  const currentExpiry =
    profile?.tgExpiryDate || profile?.nonLicenceExpiryDate || "";

  const sameExpiryDate =
    normalizeDateOnly(currentExpiry) === normalizeDateOnly(form.expiryDate);

  const daysLeft = useMemo(() => getDaysLeft(currentExpiry), [currentExpiry]);

  const isSubmitDisabled =
    submitting ||
    badgeChecking ||
    !form.photo ||
    !badgeVerified ||
    !form.expiryDate ||
    !form.confirm ||
    sameExpiryDate;

  useEffect(() => {
    if (!show) return;

    setError("");
    setBadgeChecking(false);
    setBadgeVerified(false);
    setBadgeAnalyzeResult(null);
    setSubmitDone(false);
    setSubmittedRefNo("");

    setForm({
      id: editRecord?.id || "",
      refNumber: editRecord?.refNumber || "",
      photo: null,
      photoName: "",
      expiryDate: "",
      confirm: false,
    });

    setFileMeta({
      filename: "",
      path: "",
      mimetype: "",
      size: 0,
    });

    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, editRecord]);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const closeModal = () => {
    if (submitting || badgeChecking) return;

    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl("");
    }

    onHide?.();
  };

  const analyzeSelectedBadge = async (file, userBadge) => {
    try {
      setBadgeChecking(true);
      setBadgeVerified(false);
      setBadgeAnalyzeResult(null);
      setError("");

      setForm((prev) => ({
        ...prev,
        expiryDate: "",
      }));

      const formData = new FormData();
      formData.append("file", file);
      formData.append("userBadge", String(userBadge || "").trim());

      const { data } = await axios.post(analyzeEndpoint, formData, {
        headers: {
          Authorization: `Bearer ${authToken}`,
          "x-userid": String(profile?.userId || ""),
        },
        timeout: 60000,
      });

      const aiResult = data?.data?.aiResult || null;
      const filePath = data?.data?.filePath || null;

      setBadgeAnalyzeResult(aiResult);

      setFileMeta({
        filename:
          filePath?.filename || filePath?.originalname || file?.name || "",
        path: filePath?.path || "",
        mimetype: filePath?.mimetype || file?.type || "",
        size: filePath?.size || file?.size || 0,
      });

      if (data?.success) {
        setBadgeVerified(true);

        if (aiResult?.expiration_date) {
          const parsed = new Date(aiResult.expiration_date);

          if (!Number.isNaN(parsed.getTime())) {
            const yyyy = parsed.getFullYear();
            const mm = String(parsed.getMonth() + 1).padStart(2, "0");
            const dd = String(parsed.getDate()).padStart(2, "0");

            setForm((prev) => ({
              ...prev,
              expiryDate: `${yyyy}-${mm}-${dd}`,
            }));
          }
        }
      } else {
        setBadgeVerified(false);
        setForm((prev) => ({
          ...prev,
          expiryDate: "",
        }));
      }
    } catch (err) {
      setBadgeVerified(false);
      setBadgeAnalyzeResult(null);
      setForm((prev) => ({
        ...prev,
        expiryDate: "",
      }));
      setFileMeta({
        filename: "",
        path: "",
        mimetype: "",
        size: 0,
      });
      setError(err?.response?.data?.message || "Unable to verify badge image.");
    } finally {
      setBadgeChecking(false);
    }
  };

  const handlePhoto = async (e) => {
    const file = e.target.files?.[0] || null;

    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl("");
    }

    setBadgeVerified(false);
    setBadgeAnalyzeResult(null);
    setSubmitDone(false);
    setSubmittedRefNo("");
    setError("");

    if (!file) {
      setForm((prev) => ({
        ...prev,
        photo: null,
        photoName: "",
        expiryDate: "",
      }));
      setFileMeta({
        filename: "",
        path: "",
        mimetype: "",
        size: 0,
      });
      return;
    }

    const allowedTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "application/pdf",
    ];

    if (!allowedTypes.includes(file.type)) {
      setError("Only JPG, PNG, or PDF files are allowed.");
      setForm((prev) => ({
        ...prev,
        photo: null,
        photoName: "",
        expiryDate: "",
      }));
      setFileMeta({
        filename: "",
        path: "",
        mimetype: "",
        size: 0,
      });
      return;
    }

    if (file.size > 8 * 1024 * 1024) {
      setError("Maximum file size is 8 MB.");
      setForm((prev) => ({
        ...prev,
        photo: null,
        photoName: "",
        expiryDate: "",
      }));
      setFileMeta({
        filename: "",
        path: "",
        mimetype: "",
        size: 0,
      });
      return;
    }

    setForm((prev) => ({
      ...prev,
      photo: file,
      photoName: file.name,
      expiryDate: "",
    }));

    if (file.type !== "application/pdf") {
      setPreviewUrl(URL.createObjectURL(file));
    }

    await analyzeSelectedBadge(file, profile?.badgeNo);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!form.photo) {
      setError("Please upload your badge image or PDF.");
      return;
    }

    if (
      normalizeDateOnly(currentExpiry) === normalizeDateOnly(form.expiryDate)
    ) {
      setError("New expiry date cannot be the same as current expiry date.");
      return;
    }

    if (!form.confirm) {
      setError("Please confirm the information is correct.");
      return;
    }

    if (badgeChecking) {
      setError("Badge verification is still in progress.");
      return;
    }

    if (!badgeVerified) {
      setError(
        "Please upload a valid badge image that matches your badge number.",
      );
      return;
    }

    if (!fileMeta.path) {
      setError("Saved file path was not returned by analyzeFile.");
      return;
    }

    try {
      setSubmitting(true);

      const payload = {
        id: form.id || "",
        refNumber: form.refNumber || "",
        badgeNo: profile?.badgeNo || "",
        currExpiryDate: currentExpiry || "",
        newExpiryDate: form.expiryDate,
        filename: fileMeta.filename || form.photoName || "",
        path: fileMeta.path || "",
        mimetype: fileMeta.mimetype || form.photo?.type || "",
        size: fileMeta.size || form.photo?.size || 0,
      };

      const saveRes = await axios.post(submitEndpoint, payload, {
        headers: {
          Authorization: `Bearer ${authToken}`,
          "Content-Type": "application/json",
        },
        timeout: 30000,
      });

      if (!saveRes?.data?.success) {
        throw new Error(saveRes?.data?.message || "Submit failed.");
      }

      const result = saveRes?.data?.data || {};
      const refNo = result?.refNumber || "";

      setSubmittedRefNo(refNo);
      setSubmitDone(true);

      onSubmitted?.({
        ...result,
        newExpiryDate: form.expiryDate,
      });
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err.message ||
          "Failed to submit renewal update.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (submitDone) {
    return (
      <Modal
        show={show}
        onHide={closeModal}
        centered
        size="lg"
        backdrop="static"
        className="custom-font"
      >
        <Modal.Body
          style={{
            background: "#F7F9FC",
            padding: 0,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              height: 10,
              background: SUCCESS_GREEN,
              width: "100%",
            }}
          />

          <div
            style={{
              padding: "28px 22px 24px",
              textAlign: "center",
            }}
          >
            <div
              style={{
                width: 122,
                height: 122,
                borderRadius: "50%",
                border: `3px solid ${SUCCESS_GREEN}`,
                margin: "0 auto 18px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "#EAF7F3",
              }}
            >
              <CheckCircleFill size={56} color={SUCCESS_GREEN} />
            </div>

            <div
              style={{
                fontSize: 28,
                fontWeight: 800,
                color: "#173C7D",
                marginBottom: 14,
              }}
            >
              Submission Received
            </div>

            <div
              style={{
                maxWidth: 620,
                margin: "0 auto 22px",
                fontSize: 17,
                lineHeight: 1.5,
                color: "#284A82",
              }}
            >
              Your license expiry date update request has been submitted.
              <br />
              We will review your documents within 3–5 working days. You will
              receive a confirmation email once approved.
            </div>

            <div
              style={{
                display: "inline-block",
                background: "#F4F7FC",
                border: "1px solid #D6E0F0",
                padding: "12px 18px",
                fontSize: 18,
                fontWeight: 800,
                color: "#173C7D",
                marginBottom: 24,
              }}
            >
              Your Reference Number: {submittedRefNo || "-"}
            </div>

            <div>
              <Button
                variant="primary"
                onClick={closeModal}
                style={{
                  minWidth: 140,
                  borderRadius: 10,
                  fontWeight: 700,
                }}
              >
                Close
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    );
  }

  return (
    <Modal
      show={show}
      onHide={() => !submitting && !badgeChecking && closeModal()}
      centered
      size="lg"
      backdrop="static"
      className="custom-font"
    >
      <Modal.Header closeButton={!submitting && !badgeChecking}>
        <Modal.Title style={{ fontWeight: 900, color: NAV_BLUE }}>
          Update Expiry Date
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body style={{ background: "#F7F9FC" }}>
          {error ? <Alert variant="danger">{error}</Alert> : null}

          <div
            style={{
              padding: "12px 14px",
              marginBottom: 18,
              background: "#FFFBEA",
              borderRadius: 12,
              border: "1px solid #F3E7A6",
            }}
          >
            <strong style={{ color: "#16386D" }}>
              Reminder to Tourist Guide:
            </strong>
            <span style={{ marginLeft: 8, color: "#16386D" }}>
              Please prepare a picture of your e-badge or a photo of your
              physical Tourist Guide badge before proceeding.
            </span>
          </div>

          <Row className="g-4">
            <Col md={6}>
              <div
                style={{
                  background: "#fff",
                  borderRadius: 16,
                  padding: 18,
                  border: "1px solid #E6ECF3",
                  height: "100%",
                }}
              >
                <div
                  style={{
                    fontWeight: 900,
                    fontSize: 16,
                    color: NAV_BLUE,
                    marginBottom: 14,
                  }}
                >
                  CURRENT LICENSE INFORMATION
                </div>

                <div className="mb-3">
                  <div
                    style={{ color: "#1F2937", fontSize: 13, marginBottom: 6 }}
                  >
                    Full Name
                  </div>
                  <div
                    style={{
                      fontWeight: 800,
                      fontSize: 18,
                      color: "#102A56",
                    }}
                  >
                    {profile?.fullName || "-"}
                  </div>
                </div>

                <div className="mb-3">
                  <div
                    style={{ color: "#1F2937", fontSize: 13, marginBottom: 6 }}
                  >
                    Badge Number
                  </div>

                  <div className="d-flex align-items-center gap-2">
                    {badgeChecking ? (
                      <Spinner size="sm" />
                    ) : badgeAnalyzeResult ? (
                      badgeVerified ? (
                        <CheckCircleFill color="#22C55E" size={22} />
                      ) : (
                        <XCircleFill color="#EF4444" size={22} />
                      )
                    ) : null}

                    <div
                      style={{
                        fontWeight: 800,
                        fontSize: 18,
                        color: "#102A56",
                      }}
                    >
                      {profile?.badgeNo || "-"}
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    background: "#FAFAF5",
                    padding: 16,
                    borderRadius: 12,
                    border: "1px solid #ECE7D8",
                    marginTop: 18,
                  }}
                >
                  <div
                    style={{ color: "#1F2937", fontSize: 13, marginBottom: 8 }}
                  >
                    Current Expiry Date
                  </div>

                  <div
                    style={{
                      fontWeight: 900,
                      fontSize: 18,
                      color: "#102A56",
                    }}
                  >
                    {formatDisplayDate(currentExpiry)}
                  </div>

                  {daysLeft !== null && (
                    <div
                      style={{
                        marginTop: 6,
                        color: daysLeft < 0 ? "#DC2626" : "#EF4444",
                        fontWeight: 800,
                        fontSize: 15,
                      }}
                    >
                      {daysLeft >= 0 ? `${daysLeft} days left` : "Expired"}
                    </div>
                  )}
                </div>
              </div>
            </Col>

            <Col md={6}>
              <div
                style={{
                  background: "#fff",
                  borderRadius: 16,
                  padding: 18,
                  border: "1px solid #E6ECF3",
                  height: "100%",
                }}
              >
                <div
                  style={{
                    fontWeight: 900,
                    fontSize: 16,
                    color: NAV_BLUE,
                    marginBottom: 14,
                  }}
                >
                  UPDATE FORM
                </div>

                <Form.Group className="mb-3">
                  <Form.Label
                    style={{
                      fontWeight: 800,
                      color: "#16386D",
                      fontSize: 14,
                    }}
                  >
                    Upload e-badge or photo of physical badge
                  </Form.Label>

                  <div
                    style={{
                      color: "#1F2937",
                      fontSize: 12,
                      marginBottom: 8,
                    }}
                  >
                    JPG, PNG or PDF. Maximum 8 MB.
                  </div>

                  <Form.Control
                    type="file"
                    accept=".jpg,.jpeg,.png,.pdf"
                    onChange={handlePhoto}
                    disabled={submitting || badgeChecking}
                  />

                  {form.photoName ? (
                    <div
                      style={{
                        marginTop: 8,
                        color: "#16386D",
                        fontWeight: 600,
                      }}
                    >
                      {form.photoName}
                    </div>
                  ) : null}

                  {badgeChecking ? (
                    <div
                      style={{ marginTop: 8, fontSize: 13, color: "#1F2937" }}
                    >
                      <Spinner size="sm" className="me-2" />
                      Verifying badge...
                    </div>
                  ) : null}

                  {!badgeChecking && badgeAnalyzeResult ? (
                    <div
                      style={{
                        marginTop: 10,
                        padding: "10px 12px",
                        borderRadius: 10,
                        border: badgeVerified
                          ? "1px solid #BBF7D0"
                          : "1px solid #FECACA",
                        background: badgeVerified ? "#F0FDF4" : "#FEF2F2",
                        color: badgeVerified ? "#166534" : "#B91C1C",
                        fontSize: 13,
                        fontWeight: 700,
                      }}
                    >
                      <div className="d-flex align-items-center">
                        {badgeVerified ? (
                          <CheckCircleFill className="me-2" color="#16A34A" />
                        ) : (
                          <XCircleFill className="me-2" color="#DC2626" />
                        )}
                        {badgeVerified
                          ? "Badge number matched successfully."
                          : "Badge number does not match."}
                      </div>
                    </div>
                  ) : null}

                  {previewUrl ? (
                    <div style={{ marginTop: 12 }}>
                      <div
                        style={{
                          fontSize: 12,
                          fontWeight: 800,
                          color: "#16386D",
                          marginBottom: 8,
                        }}
                      >
                        Preview Image
                      </div>
                      <div
                        style={{
                          border: "1px solid #E5E7EB",
                          borderRadius: 12,
                          padding: 8,
                          background: "#F9FAFB",
                        }}
                      >
                        <img
                          src={previewUrl}
                          alt="Badge Preview"
                          style={{
                            width: "100%",
                            maxHeight: 220,
                            objectFit: "contain",
                            borderRadius: 8,
                            display: "block",
                          }}
                        />
                      </div>
                    </div>
                  ) : null}

                  {form.photo &&
                  form.photo.type === "application/pdf" &&
                  !previewUrl ? (
                    <div
                      style={{
                        marginTop: 12,
                        padding: "12px",
                        borderRadius: 12,
                        border: "1px solid #E5E7EB",
                        background: "#F9FAFB",
                        fontSize: 13,
                        color: "#374151",
                        fontWeight: 600,
                      }}
                    >
                      PDF selected: {form.photoName}
                    </div>
                  ) : null}
                </Form.Group>

                {badgeVerified ? (
                  <Form.Group className="mb-3">
                    <Form.Label
                      style={{
                        fontWeight: 800,
                        color: "#16386D",
                        fontSize: 14,
                      }}
                    >
                      New Expiry Date
                    </Form.Label>
                    <Form.Control
                      type="date"
                      value={form.expiryDate}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          expiryDate: e.target.value,
                        }))
                      }
                      disabled={submitting}
                    />
                    {sameExpiryDate ? (
                      <div
                        style={{
                          marginTop: 6,
                          color: "#DC2626",
                          fontSize: 13,
                          fontWeight: 600,
                        }}
                      >
                        New expiry date cannot be the same as current expiry
                        date.
                      </div>
                    ) : null}
                  </Form.Group>
                ) : null}

                <Form.Group>
                  <Form.Check
                    type="checkbox"
                    label="I confirm that the information provided is correct."
                    checked={form.confirm}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        confirm: e.target.checked,
                      }))
                    }
                    disabled={submitting}
                  />
                </Form.Group>
              </div>
            </Col>
          </Row>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="light"
            onClick={closeModal}
            disabled={submitting || badgeChecking}
          >
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={isSubmitDisabled}>
            {submitting ? (
              <>
                <Spinner size="sm" className="me-2" />
                Submitting...
              </>
            ) : (
              "Submit Update"
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
